import type { PaginationOptions } from '~/modules/root/util/get-pagination-opts.server';
import type { PaginationItemCounterProps } from '@archipro-design/aria';
import { useAppDisplayModeContext, useStyles } from '@archipro-design/aria';
import { PaginationItem, Pagination, Flex } from '@archipro-design/aria';
import { Link, useLocation, useSearchParams } from '@remix-run/react';
import * as S from './BottomPagination.style';
import { pluralize } from '~/utils';

interface BottomPaginationProps {
    pagination: PaginationOptions;
    itemName?: string;
    onPageChange?: (page: number) => void;
    pinCountAfterUpload?: number;
}

const BottomPagination = ({
    itemName = 'item',
    onPageChange,
    pagination,
    pinCountAfterUpload,
}: BottomPaginationProps) => {
    const { css } = useStyles();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const appDisplayMode = useAppDisplayModeContext();

    const pinCount = pinCountAfterUpload || pagination.totalCount;

    const createPrefetchLink = (page: number): string => {
        const copyParams = new URLSearchParams(searchParams);
        copyParams.delete('page');
        if (page && page > 1) copyParams.set('page', page.toString());
        return `${location.pathname}?${copyParams.toString()}`;
    };

    return pagination.totalPages > 1 ? (
        <Flex className={css(S.PaginationWrapper)} hAlign="center" fill>
            <Pagination
                prevButton={{
                    as: Link,
                    prefetch: 'intent',
                    key: 'prevButton',
                    to: createPrefetchLink(pagination.prevPage),
                }}
                nextButton={{
                    as: Link,
                    prefetch: 'intent',
                    key: 'nextButton',
                    to: createPrefetchLink(pagination.nextPage),
                    disabled: pagination.page >= pagination.totalPages,
                }}
                renderItem={(item) => {
                    const disablePrev =
                        item.type === 'previous' && pagination.prevPage === 0;
                    const disableNext =
                        item.type === 'next' &&
                        pagination.page >= pagination.totalPages;

                    if (disablePrev || disableNext) {
                        return PaginationItem.create({
                            key: `pagination-item-${item.page}-${item.type}`,
                            content: item,
                        });
                    }

                    return PaginationItem.create({
                        key: `pagination-item-${item.page}-${item.type}`,
                        as: Link,
                        prefetch: 'intent',
                        to: createPrefetchLink(item.page),
                        content: item,
                    });
                }}
                siblingCount={1}
                boundaryCount={1}
                count={pagination.totalPages}
                counter={{
                    range: {
                        from: pagination.from,
                        to: pagination.to,
                    },
                    total: pinCount,
                    textRenderer: (
                        range: PaginationItemCounterProps['range'],
                        total: PaginationItemCounterProps['total']
                    ) =>
                        `${range.from} - ${range.to} of ${total} ${pluralize(
                            range.to - range.from,
                            itemName,
                            true
                        )}`,
                }}
                totalCount={pinCount}
                defaultPage={pagination.page}
                page={pagination.page}
                pageSize={pagination.limit}
                appDisplayMode={appDisplayMode}
                onChange={(_, page) => {
                    onPageChange && page !== null && onPageChange(page);
                }}
            />
        </Flex>
    ) : null;
};

export default BottomPagination;
